import React, { useState, useEffect } from 'react';
import TemplateDownload from './TemplateDownload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Hook for mobile detection
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isMobile;
};

function Servicing({ token, user }) {
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState('due');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  
  // Data states
  const [dueVehicles, setDueVehicles] = useState({ overdue: [], due_soon: [] });
  const [servicingRecords, setServicingRecords] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [profiles, setProfiles] = useState([]);
  
  // Modal states
  const [contactModal, setContactModal] = useState({ show: false, contact: null });
  const [recordModal, setRecordModal] = useState({ show: false });
  const [assignModal, setAssignModal] = useState({ show: false, contact: null, selectedVehicles: [] });
  const [uploadModal, setUploadModal] = useState({ show: false, type: 'records' }); // 'records' or 'schedules'
  const [profileModal, setProfileModal] = useState({ show: false, profile: null });
  
  // Profile form state
  const emptyLevel = { name: '', interval_km: '', interval_months: '', description: '', estimated_cost: '' };
  const [profileFormData, setProfileFormData] = useState({
    name: '',
    description: '',
    workshop: '',
    levels: [{ ...emptyLevel }]
  });
  
  // Form states
  const [searchQuery, setSearchQuery] = useState('');
  const [deptFilter, setDeptFilter] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dueRes, recordsRes, contactsRes, vehiclesRes, profilesRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/servicing/due?days_ahead=30`, { headers: { 'Authorization': `Bearer ${token}` }}),
        fetch(`${BACKEND_URL}/api/servicing/records?limit=50`, { headers: { 'Authorization': `Bearer ${token}` }}),
        fetch(`${BACKEND_URL}/api/servicing/contacts`, { headers: { 'Authorization': `Bearer ${token}` }}),
        fetch(`${BACKEND_URL}/api/vehicles`, { headers: { 'Authorization': `Bearer ${token}` }}),
        fetch(`${BACKEND_URL}/api/servicing-profiles`, { headers: { 'Authorization': `Bearer ${token}` }})
      ]);
      
      if (dueRes.ok) setDueVehicles(await dueRes.json());
      if (recordsRes.ok) setServicingRecords(await recordsRes.json());
      if (contactsRes.ok) setContacts(await contactsRes.json());
      if (vehiclesRes.ok) setVehicles(await vehiclesRes.json());
      if (profilesRes.ok) setProfiles(await profilesRes.json());
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load data' });
    }
    setLoading(false);
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  };

  // Get unique departments from vehicles
  const departments = [...new Set(vehicles.map(v => v.department).filter(Boolean))];

  // Filter vehicles for assignment
  const filteredVehicles = vehicles.filter(v => {
    const matchesSearch = !searchQuery || 
      v.vehicle_no.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (v.vehicle_make && v.vehicle_make.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesDept = !deptFilter || v.department === deptFilter;
    return matchesSearch && matchesDept;
  });

  // CRUD handlers
  const handleSaveContact = async () => {
    const { contact } = contactModal;
    try {
      const isEdit = contact?.id;
      const url = isEdit 
        ? `${BACKEND_URL}/api/servicing/contacts/${contact.id}`
        : `${BACKEND_URL}/api/servicing/contacts`;
      
      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: contact.name,
          email: contact.email,
          department: contact.department || null,
          project: contact.project || null,
          assigned_vehicles: contact.assigned_vehicles || []
        })
      });
      
      if (response.ok) {
        showMessage('success', `Contact ${isEdit ? 'updated' : 'created'} successfully`);
        setContactModal({ show: false, contact: null });
        fetchData();
      } else {
        const data = await response.json();
        showMessage('error', data.detail || 'Failed to save contact');
      }
    } catch (err) {
      showMessage('error', 'Failed to save contact');
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/servicing/contacts/${contactId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        showMessage('success', 'Contact deleted');
        fetchData();
      }
    } catch (err) {
      showMessage('error', 'Failed to delete contact');
    }
  };

  const handleAssignVehicles = async () => {
    const { contact, selectedVehicles } = assignModal;
    try {
      const response = await fetch(`${BACKEND_URL}/api/servicing/contacts/${contact.id}/assign-vehicles`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(selectedVehicles)
      });
      
      if (response.ok) {
        showMessage('success', `Assigned ${selectedVehicles.length} vehicles`);
        setAssignModal({ show: false, contact: null, selectedVehicles: [] });
        fetchData();
      }
    } catch (err) {
      showMessage('error', 'Failed to assign vehicles');
    }
  };

  const handleSaveRecord = async (e) => {
    e.preventDefault();
    const form = e.target;
    const formData = {
      vehicle_no: form.vehicle_no.value,
      service_type: form.service_type.value,
      service_date: form.service_date.value,
      mileage_at_service: form.mileage.value ? parseFloat(form.mileage.value) : null,
      workshop_vendor: form.workshop.value || null,
      invoice_number: form.invoice_number.value || null,
      cost: form.cost.value ? parseFloat(form.cost.value) : null,
      items_serviced: form.items.value || null,
      notes: form.notes.value || null
    };
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/servicing/records`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        showMessage('success', 'Servicing record created');
        setRecordModal({ show: false });
        fetchData();
      } else {
        const data = await response.json();
        showMessage('error', data.detail || 'Failed to create record');
      }
    } catch (err) {
      showMessage('error', 'Failed to create record');
    }
  };

  const handleBulkUpload = async (e) => {
    e.preventDefault();
    const file = e.target.file.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      setLoading(true);
      const endpoint = uploadModal.type === 'records' 
        ? '/api/servicing/records/bulk-upload'
        : '/api/servicing/schedule/bulk-update';
      
      const response = await fetch(`${BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      const data = await response.json();
      
      if (response.ok) {
        showMessage('success', `${data.inserted || data.updated} ${uploadModal.type} processed`);
        setUploadModal({ show: false, type: 'records' });
        fetchData();
      } else {
        showMessage('error', data.detail || 'Upload failed');
      }
    } catch (err) {
      showMessage('error', 'Upload failed');
    }
    setLoading(false);
  };

  const handleBookAppointment = async (vehicleNo) => {
    const date = prompt('Enter appointment date (YYYY-MM-DD):');
    if (!date) return;
    
    try {
      const formData = new FormData();
      formData.append('appointment_date', date);
      
      const response = await fetch(`${BACKEND_URL}/api/vehicles/${vehicleNo}/book-service`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        showMessage('success', `Appointment booked for ${date}`);
        fetchData();
      }
    } catch (err) {
      showMessage('error', 'Failed to book appointment');
    }
  };

  const handleSendReminders = async () => {
    if (!window.confirm('Send servicing reminder emails to all due contacts?')) return;
    
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/servicing/send-reminders`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      if (response.ok) {
        showMessage('success', `Sent ${data.emails_sent?.length || 0} reminder emails`);
      } else {
        showMessage('error', data.detail || 'Failed to send reminders');
      }
    } catch (err) {
      showMessage('error', 'Failed to send reminders');
    }
    setLoading(false);
  };

  const handleTestEmail = async () => {
    const email = prompt('Enter email to send test:');
    if (!email) return;
    
    try {
      const formData = new FormData();
      formData.append('to_email', email);
      
      const response = await fetch(`${BACKEND_URL}/api/email/test`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        showMessage('success', `Test email sent to ${email}`);
      } else {
        const data = await response.json();
        showMessage('error', data.detail || 'Failed to send test email');
      }
    } catch (err) {
      showMessage('error', 'Failed to send test email');
    }
  };

  // Profile handlers
  const openProfileModal = (profile = null) => {
    if (profile) {
      // Editing existing profile
      setProfileFormData({
        name: profile.name || '',
        description: profile.description || '',
        workshop: profile.workshop || '',
        levels: profile.levels?.length > 0 ? profile.levels.map(l => ({
          name: l.name || '',
          interval_km: l.interval_km || '',
          interval_months: l.interval_months || '',
          description: l.description || '',
          estimated_cost: l.estimated_cost || ''
        })) : [{ ...emptyLevel }]
      });
      setProfileModal({ show: true, profile });
    } else {
      // New profile
      setProfileFormData({
        name: '',
        description: '',
        workshop: '',
        levels: [{ ...emptyLevel }]
      });
      setProfileModal({ show: true, profile: null });
    }
  };

  const addServiceLevel = () => {
    if (profileFormData.levels.length < 4) {
      setProfileFormData({
        ...profileFormData,
        levels: [...profileFormData.levels, { ...emptyLevel }]
      });
    }
  };

  const removeServiceLevel = (index) => {
    if (profileFormData.levels.length > 1) {
      setProfileFormData({
        ...profileFormData,
        levels: profileFormData.levels.filter((_, i) => i !== index)
      });
    }
  };

  const updateServiceLevel = (index, field, value) => {
    const newLevels = [...profileFormData.levels];
    newLevels[index] = { ...newLevels[index], [field]: value };
    setProfileFormData({ ...profileFormData, levels: newLevels });
  };

  const handleSaveProfile = async () => {
    // Validate
    if (!profileFormData.name.trim()) {
      showMessage('error', 'Profile name is required');
      return;
    }
    
    const validLevels = profileFormData.levels.filter(l => l.name.trim() && (l.interval_km || l.interval_months));
    if (validLevels.length === 0) {
      showMessage('error', 'At least one service level with name and interval is required');
      return;
    }
    
    const payload = {
      name: profileFormData.name.trim(),
      description: profileFormData.description.trim(),
      workshop: profileFormData.workshop.trim(),
      levels: validLevels.map(l => ({
        name: l.name.trim(),
        interval_km: parseInt(l.interval_km) || 0,
        interval_months: parseInt(l.interval_months) || 0,
        description: l.description.trim(),
        estimated_cost: l.estimated_cost ? parseFloat(l.estimated_cost) : null
      }))
    };
    
    try {
      const isEdit = profileModal.profile?.profile_id;
      const url = isEdit 
        ? `${BACKEND_URL}/api/servicing-profiles/${profileModal.profile.profile_id}`
        : `${BACKEND_URL}/api/servicing-profiles`;
      
      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        showMessage('success', `Profile ${isEdit ? 'updated' : 'created'} successfully`);
        setProfileModal({ show: false, profile: null });
        fetchData();
      } else {
        const data = await response.json();
        showMessage('error', data.detail || 'Failed to save profile');
      }
    } catch (err) {
      showMessage('error', 'Failed to save profile');
    }
  };

  const handleDeleteProfile = async (profileId) => {
    if (!window.confirm('Delete this servicing profile? This cannot be undone.')) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/servicing-profiles/${profileId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        showMessage('success', 'Profile deleted');
        fetchData();
      } else {
        const data = await response.json();
        showMessage('error', data.detail || 'Failed to delete profile');
      }
    } catch (err) {
      showMessage('error', 'Failed to delete profile');
    }
  };

  const handleSeedDefaults = async () => {
    if (!window.confirm('This will add default profiles (MAN, Hino, Standard). Continue?')) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/servicing-profiles/seed-defaults`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        showMessage('success', `Added ${data.inserted} new profiles, updated ${data.updated}`);
        fetchData();
      } else {
        showMessage('error', 'Failed to seed default profiles');
      }
    } catch (err) {
      showMessage('error', 'Failed to seed default profiles');
    }
  };

  // Tab content components
  const renderDueTab = () => (
    <div data-testid="due-servicing-tab">
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <button 
          className="btn btn-primary"
          onClick={handleSendReminders}
          disabled={loading}
          data-testid="send-reminders-btn"
        >
          📧 Send Reminder Emails
        </button>
        <button 
          className="btn btn-secondary"
          onClick={handleTestEmail}
          data-testid="test-email-btn"
        >
          🧪 Test Email
        </button>
      </div>
      
      {/* Overdue Section */}
      {dueVehicles.overdue?.length > 0 && (
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ color: '#DC2626', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ background: '#DC2626', color: 'white', padding: '0.25rem 0.75rem', borderRadius: '100px', fontSize: '0.8rem' }}>
              {dueVehicles.overdue.length}
            </span>
            Overdue
          </h3>
          <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))' }}>
            {dueVehicles.overdue.map(v => (
              <DueVehicleCard key={v.vehicle_no} vehicle={v} onBook={handleBookAppointment} type="overdue" />
            ))}
          </div>
        </div>
      )}
      
      {/* Due Soon Section */}
      {dueVehicles.due_soon?.length > 0 && (
        <div>
          <h3 style={{ color: '#D97706', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ background: '#D97706', color: 'white', padding: '0.25rem 0.75rem', borderRadius: '100px', fontSize: '0.8rem' }}>
              {dueVehicles.due_soon.length}
            </span>
            Due Soon (within 30 days)
          </h3>
          <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))' }}>
            {dueVehicles.due_soon.map(v => (
              <DueVehicleCard key={v.vehicle_no} vehicle={v} onBook={handleBookAppointment} type="due" />
            ))}
          </div>
        </div>
      )}
      
      {!dueVehicles.overdue?.length && !dueVehicles.due_soon?.length && (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#64748B' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
          <p>No vehicles due for servicing in the next 30 days</p>
          <p style={{ fontSize: '0.85rem' }}>Set up service intervals in Vehicle details to track schedules</p>
        </div>
      )}
    </div>
  );

  const renderRecordsTab = () => (
    <div data-testid="records-tab">
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <button 
          className="btn btn-primary"
          onClick={() => setRecordModal({ show: true })}
          data-testid="add-record-btn"
        >
          ➕ Add Record
        </button>
        <button 
          className="btn btn-secondary"
          onClick={() => setUploadModal({ show: true, type: 'records' })}
          data-testid="upload-records-btn"
        >
          📤 Bulk Upload Records
        </button>
        <button 
          className="btn btn-secondary"
          onClick={() => setUploadModal({ show: true, type: 'schedules' })}
          data-testid="upload-schedules-btn"
        >
          📤 Update Schedules
        </button>
      </div>
      
      {servicingRecords.length > 0 ? (
        <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(350px, 1fr))' }}>
          {servicingRecords.map(record => (
            <div 
              key={record.id} 
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '1rem',
                border: '1px solid #E2E8F0',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h4 style={{ color: '#003366', margin: 0, fontFamily: "'JetBrains Mono', monospace" }}>
                    {record.vehicle_no}
                  </h4>
                  <p style={{ color: '#64748B', fontSize: '0.85rem', margin: '0.25rem 0' }}>
                    {record.service_date} • {record.service_type}
                  </p>
                </div>
                {record.cost && (
                  <span style={{ 
                    background: '#F1F5F9', 
                    padding: '0.25rem 0.75rem', 
                    borderRadius: '100px',
                    fontWeight: '600',
                    color: '#003366'
                  }}>
                    ${record.cost.toFixed(2)}
                  </span>
                )}
              </div>
              {record.mileage_at_service && (
                <p style={{ fontSize: '0.85rem', color: '#64748B', margin: '0.5rem 0 0' }}>
                  📏 {record.mileage_at_service.toLocaleString()} km
                </p>
              )}
              {record.workshop_vendor && (
                <p style={{ fontSize: '0.85rem', color: '#64748B', margin: '0.25rem 0 0' }}>
                  🔧 {record.workshop_vendor}
                </p>
              )}
              {record.items_serviced && (
                <p style={{ fontSize: '0.85rem', color: '#64748B', margin: '0.25rem 0 0' }}>
                  📝 {record.items_serviced}
                </p>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#64748B' }}>
          <p>No servicing records yet</p>
          <p style={{ fontSize: '0.85rem' }}>Add records manually or bulk upload from Excel/CSV</p>
        </div>
      )}
    </div>
  );

  const renderContactsTab = () => (
    <div data-testid="contacts-tab">
      <div style={{ marginBottom: '1.5rem' }}>
        <button 
          className="btn btn-primary"
          onClick={() => setContactModal({ show: true, contact: { name: '', email: '', department: '', project: '', assigned_vehicles: [] } })}
          data-testid="add-contact-btn"
        >
          ➕ Add Contact
        </button>
      </div>
      
      {contacts.length > 0 ? (
        <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(350px, 1fr))' }}>
          {contacts.map(contact => (
            <div 
              key={contact.id}
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '1.25rem',
                border: '1px solid #E2E8F0',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h4 style={{ color: '#1E293B', margin: 0 }}>{contact.name}</h4>
                  <p style={{ color: '#64748B', fontSize: '0.85rem', margin: '0.25rem 0' }}>
                    📧 {contact.email}
                  </p>
                  {contact.department && (
                    <p style={{ color: '#64748B', fontSize: '0.85rem', margin: '0.25rem 0' }}>
                      🏢 {contact.department} {contact.project && `/ ${contact.project}`}
                    </p>
                  )}
                </div>
                <span style={{
                  background: contact.assigned_vehicles?.length > 0 ? '#D1FAE5' : '#FEE2E2',
                  color: contact.assigned_vehicles?.length > 0 ? '#059669' : '#DC2626',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '100px',
                  fontSize: '0.75rem',
                  fontWeight: '600'
                }}>
                  {contact.assigned_vehicles?.length || 0} vehicles
                </span>
              </div>
              
              {contact.assigned_vehicles?.length > 0 && (
                <div style={{ marginTop: '0.75rem', display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                  {contact.assigned_vehicles.slice(0, 5).map(v => (
                    <span key={v} style={{
                      background: '#F1F5F9',
                      padding: '0.2rem 0.5rem',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      fontFamily: "'JetBrains Mono', monospace"
                    }}>
                      {v}
                    </span>
                  ))}
                  {contact.assigned_vehicles.length > 5 && (
                    <span style={{ fontSize: '0.75rem', color: '#64748B' }}>
                      +{contact.assigned_vehicles.length - 5} more
                    </span>
                  )}
                </div>
              )}
              
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                <button
                  className="btn btn-sm"
                  style={{ background: '#003366', color: 'white', flex: 1 }}
                  onClick={() => {
                    setAssignModal({ 
                      show: true, 
                      contact, 
                      selectedVehicles: contact.assigned_vehicles || [] 
                    });
                    setSearchQuery('');
                    setDeptFilter('');
                  }}
                >
                  🚛 Assign Vehicles
                </button>
                <button
                  className="btn btn-sm"
                  style={{ background: '#F1F5F9', color: '#334155' }}
                  onClick={() => setContactModal({ show: true, contact })}
                >
                  ✏️
                </button>
                <button
                  className="btn btn-sm"
                  style={{ background: '#FEE2E2', color: '#DC2626' }}
                  onClick={() => handleDeleteContact(contact.id)}
                >
                  🗑️
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#64748B' }}>
          <p>No servicing contacts yet</p>
          <p style={{ fontSize: '0.85rem' }}>Add contacts who will receive servicing reminder emails</p>
        </div>
      )}
    </div>
  );

  // Profiles tab content
  const renderProfilesTab = () => (
    <div data-testid="profiles-tab">
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <button 
          className="btn btn-primary"
          onClick={() => openProfileModal()}
          data-testid="add-profile-btn"
        >
          ➕ New Profile
        </button>
        <button 
          className="btn btn-secondary"
          onClick={handleSeedDefaults}
          data-testid="seed-defaults-btn"
        >
          📦 Load Default Profiles
        </button>
      </div>
      
      {/* Profiles grid - only show multi-level profiles */}
      {profiles.filter(p => p.levels && p.levels.length > 0).length > 0 ? (
        <div style={{ 
          display: 'grid', 
          gap: '1rem', 
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(400px, 1fr))'
        }}>
          {profiles.filter(p => p.levels && p.levels.length > 0).map(profile => (
            <div
              key={profile.profile_id}
              data-testid={`profile-card-${profile.profile_id}`}
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '1.25rem',
                border: '1px solid #E2E8F0',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                <div>
                  <h4 style={{ color: '#003366', margin: 0 }}>{profile.name}</h4>
                  {profile.workshop && (
                    <span style={{ fontSize: '0.8rem', color: '#64748B' }}>🔧 {profile.workshop}</span>
                  )}
                </div>
                <span style={{
                  background: '#DBEAFE',
                  color: '#1D4ED8',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '100px',
                  fontSize: '0.75rem',
                  fontWeight: '600'
                }}>
                  {profile.levels?.length || 0} levels
                </span>
              </div>
              
              {profile.description && (
                <p style={{ color: '#64748B', fontSize: '0.85rem', margin: '0.5rem 0' }}>
                  {profile.description}
                </p>
              )}
              
              {/* Service Levels */}
              <div style={{ marginTop: '1rem' }}>
                {profile.levels?.map((level, idx) => (
                  <div key={idx} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.6rem 0',
                    borderTop: idx > 0 ? '1px solid #F1F5F9' : 'none'
                  }}>
                    <span style={{
                      background: ['#EEF2FF', '#F0FDF4', '#FEF3C7', '#FEE2E2'][idx % 4],
                      color: ['#4F46E5', '#16A34A', '#D97706', '#DC2626'][idx % 4],
                      padding: '0.35rem 0.75rem',
                      borderRadius: '6px',
                      fontSize: '0.85rem',
                      fontWeight: '700',
                      minWidth: '40px',
                      textAlign: 'center'
                    }}>
                      {level.name}
                    </span>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontWeight: '600', color: '#334155', fontSize: '0.9rem' }}>
                        {level.interval_km?.toLocaleString()} km
                      </span>
                      <span style={{ color: '#64748B', fontSize: '0.85rem', marginLeft: '0.5rem' }}>
                        / {level.interval_months} months
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                <button
                  className="btn btn-sm"
                  style={{ background: '#F1F5F9', color: '#334155', flex: 1 }}
                  onClick={() => openProfileModal(profile)}
                  data-testid={`edit-profile-${profile.profile_id}`}
                >
                  ✏️ Edit
                </button>
                <button
                  className="btn btn-sm"
                  style={{ background: '#FEE2E2', color: '#DC2626' }}
                  onClick={() => handleDeleteProfile(profile.profile_id)}
                  data-testid={`delete-profile-${profile.profile_id}`}
                >
                  🗑️
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ 
          textAlign: 'center', 
          padding: '3rem', 
          color: '#64748B',
          background: '#F8FAFC',
          borderRadius: '12px'
        }}>
          <p style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>No multi-level profiles yet</p>
          <p style={{ fontSize: '0.85rem', marginBottom: '1rem' }}>
            Create profiles with A/B/C/D service levels, or load defaults for common manufacturers
          </p>
          <button className="btn btn-primary" onClick={handleSeedDefaults}>
            📦 Load Default Profiles (MAN, Hino, Standard)
          </button>
        </div>
      )}
    </div>
  );

  // Sub-component for due vehicle cards
  const DueVehicleCard = ({ vehicle, onBook, type }) => (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: '1rem',
      border: `2px solid ${type === 'overdue' ? '#FECACA' : '#FED7AA'}`,
      boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h4 style={{ color: '#003366', margin: 0, fontFamily: "'JetBrains Mono', monospace" }}>
            {vehicle.vehicle_no}
          </h4>
          <p style={{ color: '#64748B', fontSize: '0.85rem', margin: '0.25rem 0' }}>
            {vehicle.department} {vehicle.project && `/ ${vehicle.project}`}
          </p>
        </div>
        <span style={{
          background: type === 'overdue' ? '#DC2626' : '#D97706',
          color: 'white',
          padding: '0.25rem 0.75rem',
          borderRadius: '100px',
          fontSize: '0.75rem',
          fontWeight: '700'
        }}>
          {type === 'overdue' ? `${Math.abs(vehicle.days_until_due)} days overdue` : `${vehicle.days_until_due} days`}
        </span>
      </div>
      
      <div style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: '#475569' }}>
        <p style={{ margin: '0.25rem 0' }}>📅 Due: {vehicle.due_date}</p>
        {vehicle.next_service_due_km && (
          <p style={{ margin: '0.25rem 0' }}>📏 Due at: {vehicle.next_service_due_km.toLocaleString()} km</p>
        )}
        {vehicle.current_mileage && (
          <p style={{ margin: '0.25rem 0' }}>🚗 Current: {vehicle.current_mileage.toLocaleString()} km</p>
        )}
        {vehicle.contact && (
          <p style={{ margin: '0.25rem 0' }}>👤 {vehicle.contact.name} ({vehicle.contact.email})</p>
        )}
      </div>
      
      <button
        className="btn btn-sm"
        style={{ marginTop: '0.75rem', background: '#003366', color: 'white', width: '100%' }}
        onClick={() => onBook(vehicle.vehicle_no)}
      >
        📅 Book Appointment
      </button>
    </div>
  );

  return (
    <div data-testid="servicing-page" style={{ maxWidth: '100%' }}>
      {/* Header */}
      <div className="page-header" style={{ marginBottom: '1.5rem' }}>
        <h2 data-testid="servicing-title">🛠️ Servicing Management</h2>
        <p style={{ color: '#64748B' }}>Track vehicle servicing schedules and send reminders</p>
      </div>
      
      {/* Message */}
      {message && (
        <div className={`message ${message.type}`} style={{ marginBottom: '1rem' }}>
          {message.text}
        </div>
      )}
      
      {/* Tabs */}
      <div style={{ 
        display: 'flex', 
        gap: '0.5rem', 
        marginBottom: '1.5rem',
        flexWrap: 'wrap'
      }}>
        {[
          { id: 'due', label: '📅 Due Services', count: (dueVehicles.overdue?.length || 0) + (dueVehicles.due_soon?.length || 0) },
          { id: 'records', label: '📝 Records', count: servicingRecords.length },
          { id: 'contacts', label: '👤 Contacts', count: contacts.length },
          { id: 'profiles', label: '📋 Profiles', count: profiles.filter(p => p.levels?.length > 0).length }
        ].map(tab => (
          <button
            key={tab.id}
            data-testid={`tab-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '0.75rem 1.25rem',
              borderRadius: '8px',
              border: activeTab === tab.id ? '2px solid #003366' : '1px solid #E2E8F0',
              background: activeTab === tab.id ? '#003366' : 'white',
              color: activeTab === tab.id ? 'white' : '#64748B',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            {tab.label}
            {tab.count > 0 && (
              <span style={{
                background: activeTab === tab.id ? 'white' : '#003366',
                color: activeTab === tab.id ? '#003366' : 'white',
                padding: '0.1rem 0.5rem',
                borderRadius: '100px',
                fontSize: '0.75rem',
                fontWeight: '700'
              }}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>
      
      {/* Loading */}
      {loading && <div className="spinner"></div>}
      
      {/* Tab Content */}
      {!loading && (
        <>
          {activeTab === 'due' && renderDueTab()}
          {activeTab === 'records' && renderRecordsTab()}
          {activeTab === 'contacts' && renderContactsTab()}
          {activeTab === 'profiles' && renderProfilesTab()}
        </>
      )}
      
      {/* Contact Modal */}
      {contactModal.show && (
        <div className="modal-overlay" onClick={() => setContactModal({ show: false, contact: null })}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <h3>{contactModal.contact?.id ? 'Edit Contact' : 'Add Servicing Contact'}</h3>
            
            <div className="form-group">
              <label className="form-label">Name *</label>
              <input
                type="text"
                className="form-input"
                value={contactModal.contact?.name || ''}
                onChange={e => setContactModal({
                  ...contactModal,
                  contact: { ...contactModal.contact, name: e.target.value }
                })}
                placeholder="John Smith"
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Email *</label>
              <input
                type="email"
                className="form-input"
                value={contactModal.contact?.email || ''}
                onChange={e => setContactModal({
                  ...contactModal,
                  contact: { ...contactModal.contact, email: e.target.value }
                })}
                placeholder="john@company.com"
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Department</label>
              <select
                className="form-input"
                value={contactModal.contact?.department || ''}
                onChange={e => setContactModal({
                  ...contactModal,
                  contact: { ...contactModal.contact, department: e.target.value }
                })}
              >
                <option value="">Select Department</option>
                {departments.map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label className="form-label">Project (Optional)</label>
              <input
                type="text"
                className="form-input"
                value={contactModal.contact?.project || ''}
                onChange={e => setContactModal({
                  ...contactModal,
                  contact: { ...contactModal.contact, project: e.target.value }
                })}
                placeholder="Project name"
              />
            </div>
            
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem' }}>
              <button className="btn btn-primary" onClick={handleSaveContact} style={{ flex: 1 }}>
                💾 Save Contact
              </button>
              <button className="btn btn-secondary" onClick={() => setContactModal({ show: false, contact: null })}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Assign Vehicles Modal */}
      {assignModal.show && (
        <div className="modal-overlay" onClick={() => setAssignModal({ show: false, contact: null, selectedVehicles: [] })}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px', maxHeight: '80vh', overflow: 'auto' }}>
            <h3>Assign Vehicles to {assignModal.contact?.name}</h3>
            
            {/* Search and Filter */}
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
              <input
                type="text"
                className="form-input"
                placeholder="Search vehicle..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                style={{ flex: 1, minWidth: '150px' }}
              />
              <select
                className="form-input"
                value={deptFilter}
                onChange={e => setDeptFilter(e.target.value)}
                style={{ width: 'auto' }}
              >
                <option value="">All Departments</option>
                {departments.map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
            
            {/* Selection summary */}
            <div style={{ 
              background: '#F1F5F9', 
              padding: '0.75rem', 
              borderRadius: '8px', 
              marginBottom: '1rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span>{assignModal.selectedVehicles.length} vehicles selected</span>
              <button 
                className="btn btn-sm"
                onClick={() => setAssignModal({ ...assignModal, selectedVehicles: [] })}
              >
                Clear All
              </button>
            </div>
            
            {/* Vehicle list */}
            <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #E2E8F0', borderRadius: '8px' }}>
              {filteredVehicles.map(v => {
                const isSelected = assignModal.selectedVehicles.includes(v.vehicle_no);
                return (
                  <div
                    key={v.vehicle_no}
                    onClick={() => {
                      const newSelection = isSelected
                        ? assignModal.selectedVehicles.filter(vn => vn !== v.vehicle_no)
                        : [...assignModal.selectedVehicles, v.vehicle_no];
                      setAssignModal({ ...assignModal, selectedVehicles: newSelection });
                    }}
                    style={{
                      padding: '0.75rem 1rem',
                      borderBottom: '1px solid #E2E8F0',
                      cursor: 'pointer',
                      background: isSelected ? '#E0F2FE' : 'white',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}}
                      style={{ width: '18px', height: '18px' }}
                    />
                    <div style={{ flex: 1 }}>
                      <span style={{ fontWeight: '600', fontFamily: "'JetBrains Mono', monospace" }}>
                        {v.vehicle_no}
                      </span>
                      <span style={{ color: '#64748B', fontSize: '0.85rem', marginLeft: '0.75rem' }}>
                        {v.department} {v.project && `/ ${v.project}`}
                      </span>
                    </div>
                    <span style={{ color: '#64748B', fontSize: '0.8rem' }}>
                      {v.vehicle_make} {v.vehicle_model}
                    </span>
                  </div>
                );
              })}
            </div>
            
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem' }}>
              <button className="btn btn-primary" onClick={handleAssignVehicles} style={{ flex: 1 }}>
                ✅ Assign {assignModal.selectedVehicles.length} Vehicles
              </button>
              <button 
                className="btn btn-secondary" 
                onClick={() => setAssignModal({ show: false, contact: null, selectedVehicles: [] })}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Add Record Modal */}
      {recordModal.show && (
        <div className="modal-overlay" onClick={() => setRecordModal({ show: false })}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <h3>Add Servicing Record</h3>
            <form onSubmit={handleSaveRecord}>
              <div className="form-group">
                <label className="form-label">Vehicle *</label>
                <select name="vehicle_no" className="form-input" required>
                  <option value="">Select Vehicle</option>
                  {vehicles.map(v => (
                    <option key={v.vehicle_no} value={v.vehicle_no}>
                      {v.vehicle_no} - {v.department}
                    </option>
                  ))}
                </select>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div className="form-group">
                  <label className="form-label">Service Type *</label>
                  <select name="service_type" className="form-input" required>
                    <option value="Regular">Regular</option>
                    <option value="Major">Major</option>
                    <option value="Inspection">Inspection</option>
                    <option value="Repair">Repair</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Service Date *</label>
                  <input 
                    type="date" 
                    name="service_date" 
                    className="form-input" 
                    required 
                    defaultValue={new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div className="form-group">
                  <label className="form-label">Mileage (km)</label>
                  <input type="number" name="mileage" className="form-input" placeholder="e.g., 85000" />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Cost ($)</label>
                  <input type="number" step="0.01" name="cost" className="form-input" placeholder="e.g., 450.00" />
                </div>
              </div>
              
              <div className="form-group">
                <label className="form-label">Workshop / Vendor</label>
                <input type="text" name="workshop" className="form-input" placeholder="Workshop name" />
              </div>
              
              <div className="form-group">
                <label className="form-label">Invoice Number</label>
                <input type="text" name="invoice_number" className="form-input" placeholder="INV-12345" />
              </div>
              
              <div className="form-group">
                <label className="form-label">Items Serviced</label>
                <input type="text" name="items" className="form-input" placeholder="Oil change, filters, brake check" />
              </div>
              
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea name="notes" className="form-input" rows="2" placeholder="Additional notes..." />
              </div>
              
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>
                  💾 Save Record
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setRecordModal({ show: false })}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Upload Modal */}
      {uploadModal.show && (
        <div className="modal-overlay" onClick={() => setUploadModal({ show: false, type: 'records' })}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <h3>
              {uploadModal.type === 'records' ? '📤 Bulk Upload Servicing Records' : '📤 Bulk Update Schedules'}
            </h3>
            
            {/* Template Download Section */}
            <div style={{ 
              background: '#EEF2FF', 
              padding: '0.75rem 1rem', 
              borderRadius: '6px', 
              marginBottom: '1rem',
              border: '1px solid #C7D2FE'
            }}>
              <TemplateDownload templateType="servicing" token={token} compact={true} label="Download Sample Template" />
            </div>
            
            <div style={{ background: '#F1F5F9', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
              <p style={{ fontWeight: '600', marginBottom: '0.5rem' }}>
                {uploadModal.type === 'records' ? 'Expected Columns:' : 'Expected Columns:'}
              </p>
              {uploadModal.type === 'records' ? (
                <ul style={{ fontSize: '0.85rem', color: '#475569', margin: 0, paddingLeft: '1.25rem' }}>
                  <li>vehicle_no (required)</li>
                  <li>service_date (required)</li>
                  <li>service_type (required)</li>
                  <li>mileage (optional)</li>
                  <li>workshop (optional)</li>
                  <li>invoice_number (optional)</li>
                  <li>cost (optional)</li>
                  <li>items (optional)</li>
                </ul>
              ) : (
                <ul style={{ fontSize: '0.85rem', color: '#475569', margin: 0, paddingLeft: '1.25rem' }}>
                  <li>vehicle_no (required)</li>
                  <li>service_interval_months (optional)</li>
                  <li>service_interval_km (optional)</li>
                  <li>last_service_date (optional)</li>
                  <li>last_service_mileage (optional)</li>
                </ul>
              )}
            </div>
            
            <form onSubmit={handleBulkUpload}>
              <div className="form-group">
                <label className="form-label">Upload Excel or CSV file</label>
                <input 
                  type="file" 
                  name="file" 
                  className="form-input"
                  accept=".xlsx,.xls,.csv"
                  required
                />
              </div>
              
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={loading}>
                  {loading ? 'Uploading...' : '📤 Upload'}
                </button>
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setUploadModal({ show: false, type: 'records' })}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Profile Modal */}
      {profileModal.show && (
        <div className="modal-overlay" onClick={() => setProfileModal({ show: false, profile: null })}>
          <div 
            className="modal-content" 
            onClick={e => e.stopPropagation()} 
            style={{ maxWidth: '650px', maxHeight: '90vh', overflow: 'auto' }}
            data-testid="profile-modal"
          >
            <h3 style={{ marginBottom: '1.5rem' }}>
              {profileModal.profile ? '✏️ Edit Servicing Profile' : '➕ Create Servicing Profile'}
            </h3>
            
            {/* Profile Info */}
            <div className="form-group">
              <label className="form-label">Profile Name *</label>
              <input
                type="text"
                className="form-input"
                value={profileFormData.name}
                onChange={e => setProfileFormData({ ...profileFormData, name: e.target.value })}
                placeholder="e.g., MAN Trucks, Hino Standard"
                data-testid="profile-name-input"
              />
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <div className="form-group">
                <label className="form-label">Workshop (Optional)</label>
                <input
                  type="text"
                  className="form-input"
                  value={profileFormData.workshop}
                  onChange={e => setProfileFormData({ ...profileFormData, workshop: e.target.value })}
                  placeholder="e.g., MAN Authorized"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <input
                  type="text"
                  className="form-input"
                  value={profileFormData.description}
                  onChange={e => setProfileFormData({ ...profileFormData, description: e.target.value })}
                  placeholder="Brief description"
                />
              </div>
            </div>
            
            {/* Service Levels */}
            <div style={{ 
              marginTop: '1.5rem', 
              borderTop: '1px solid #E2E8F0', 
              paddingTop: '1.5rem' 
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h4 style={{ margin: 0, color: '#003366' }}>Service Levels</h4>
                {profileFormData.levels.length < 4 && (
                  <button 
                    type="button"
                    className="btn btn-sm" 
                    onClick={addServiceLevel}
                    style={{ background: '#003366', color: 'white' }}
                    data-testid="add-level-btn"
                  >
                    ➕ Add Level
                  </button>
                )}
              </div>
              
              <p style={{ fontSize: '0.85rem', color: '#64748B', marginBottom: '1rem' }}>
                Define up to 4 service levels (e.g., A, B, C, D or Minor, Major)
              </p>
              
              {profileFormData.levels.map((level, idx) => (
                <div 
                  key={idx} 
                  style={{ 
                    background: '#F8FAFC', 
                    borderRadius: '10px', 
                    padding: '1rem', 
                    marginBottom: '0.75rem',
                    border: '1px solid #E2E8F0'
                  }}
                  data-testid={`level-${idx}`}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                    <span style={{ 
                      background: ['#EEF2FF', '#F0FDF4', '#FEF3C7', '#FEE2E2'][idx % 4],
                      color: ['#4F46E5', '#16A34A', '#D97706', '#DC2626'][idx % 4],
                      padding: '0.25rem 0.75rem',
                      borderRadius: '6px',
                      fontWeight: '700',
                      fontSize: '0.85rem'
                    }}>
                      Level {idx + 1}
                    </span>
                    {profileFormData.levels.length > 1 && (
                      <button 
                        type="button"
                        className="btn btn-sm" 
                        onClick={() => removeServiceLevel(idx)}
                        style={{ background: '#FEE2E2', color: '#DC2626', padding: '0.25rem 0.5rem' }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label" style={{ fontSize: '0.75rem' }}>Name *</label>
                      <input
                        type="text"
                        className="form-input"
                        style={{ padding: '0.5rem' }}
                        value={level.name}
                        onChange={e => updateServiceLevel(idx, 'name', e.target.value)}
                        placeholder="A"
                        data-testid={`level-${idx}-name`}
                      />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label" style={{ fontSize: '0.75rem' }}>Interval (km) *</label>
                      <input
                        type="number"
                        className="form-input"
                        style={{ padding: '0.5rem' }}
                        value={level.interval_km}
                        onChange={e => updateServiceLevel(idx, 'interval_km', e.target.value)}
                        placeholder="20000"
                        data-testid={`level-${idx}-km`}
                      />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label" style={{ fontSize: '0.75rem' }}>Interval (months)</label>
                      <input
                        type="number"
                        className="form-input"
                        style={{ padding: '0.5rem' }}
                        value={level.interval_months}
                        onChange={e => updateServiceLevel(idx, 'interval_months', e.target.value)}
                        placeholder="3"
                        data-testid={`level-${idx}-months`}
                      />
                    </div>
                  </div>
                  
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label" style={{ fontSize: '0.75rem' }}>Description / Items Covered</label>
                    <textarea
                      className="form-input"
                      style={{ padding: '0.5rem', minHeight: '60px', resize: 'vertical' }}
                      value={level.description}
                      onChange={e => updateServiceLevel(idx, 'description', e.target.value)}
                      placeholder="e.g., Oil change, filter check, fluid top-up..."
                      data-testid={`level-${idx}-description`}
                    />
                  </div>
                </div>
              ))}
            </div>
            
            {/* Actions */}
            <div style={{ 
              display: 'flex', 
              gap: '0.5rem', 
              marginTop: '1.5rem',
              position: 'sticky',
              bottom: 0,
              background: 'white',
              paddingTop: '1rem',
              borderTop: '1px solid #E2E8F0'
            }}>
              <button 
                type="button" 
                className="btn btn-primary" 
                onClick={handleSaveProfile}
                style={{ flex: 1 }}
                data-testid="save-profile-btn"
              >
                {profileModal.profile ? '💾 Update Profile' : '✅ Create Profile'}
              </button>
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={() => setProfileModal({ show: false, profile: null })}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Servicing;
